import { isAxiosError } from 'axios';

import getErrorMessage from '~/utils/getErrorMessage';

import type { IncentiveStatus } from './types';

export const STATUS_LABELS: Record<IncentiveStatus, string> = {
  ACTIVE: 'Ativo',
  ARCHIVED: 'Arquivado',
  UNDER_ANALYSIS: 'Em Análise',
};

export interface IncentiveStatusStyle {
  bg: string;
  color: string;
  dot: string;
}

export const STATUS_STYLES: Record<IncentiveStatus, IncentiveStatusStyle> = {
  ACTIVE: { bg: '#e6f8f0', color: '#047857', dot: '#00b569' },
  ARCHIVED: { bg: '#f5f6f8', color: '#6b7280', dot: '#9ca3af' },
  UNDER_ANALYSIS: { bg: '#dbeafe', color: '#1e40af', dot: '#3b82f6' },
};

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UTC_DATE_BOUNDARY_RE = /^(\d{4})-(\d{2})-(\d{2})T(?:00:00:00|23:59:59)(?:\.\d+)?Z$/;

export interface ParsedIncentiveError {
  fieldErrors?: Record<string, string[]>;
  kind: 'conflict' | 'forbidden' | 'not-found' | 'unknown' | 'validation';
  message: string;
}

/**
 * The date-parsing helpers used to be standalone functions, but a real forward
 * dependency between them (this function needs both a "parse a plain YYYY-MM-DD
 * string" step and a "render Y/M/D as pt-BR" step) can never be satisfied at the
 * same time as this project's alphabetical export ordering — so the two are kept
 * as inline closures scoped to the one function that uses them instead.
 */
export function formatIncentiveDate(value: null | string | undefined): string {
  if (!value) return '—';

  const formatParts = (year: number, month: number, day: number): string => (
    new Date(year, month - 1, day).toLocaleDateString('pt-BR')
  );

  const parseDateOnly = (dateOnly: string): Date | null => {
    const match = DATE_ONLY_RE.exec(dateOnly);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() !== year
      || date.getMonth() !== month - 1
      || date.getDate() !== day
    ) {
      return null;
    }

    return date;
  };

  const localDate = parseDateOnly(value);
  if (localDate) {
    return localDate.toLocaleDateString('pt-BR');
  }

  const boundaryMatch = UTC_DATE_BOUNDARY_RE.exec(value);
  if (boundaryMatch) {
    return formatParts(
      Number(boundaryMatch[1]),
      Number(boundaryMatch[2]),
      Number(boundaryMatch[3]),
    );
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';

  return parsed.toLocaleDateString('pt-BR');
}

export function formatIncentiveNumber(value: null | number | undefined): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR');
}

interface IncentiveProblemDetail {
  detail?: string;
  status?: number;
  title?: string;
  type?: string;
  validation_errors?: Record<string, string[]>;
}

/**
 * Parses an error thrown by a campaign-management incentive call into a UI-friendly shape.
 *
 * The ProblemDetail (`{ detail, instance, status, title, type, validation_errors? }`) has
 * shown up in two different places depending on the endpoint: nested under an
 * `{ success, message, data }` envelope's `data` key (as `CreateCampaignRejectedError`
 * reads it for the 422 case elsewhere in this codebase), or directly at the top level of
 * `response.data` (confirmed for the archive endpoint's 409). Both are checked, preferring
 * whichever actually carries `type`/`detail`.
 */
export function parseIncentiveApiError(error: unknown): ParsedIncentiveError {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const envelope = error.response?.data as undefined | (IncentiveProblemDetail & { data?: IncentiveProblemDetail; message?: string });
    const nested = envelope?.data;
    const problem = (nested?.type !== undefined || nested?.detail !== undefined) ? nested : envelope;

    if (status === 403) {
      return { kind: 'forbidden', message: 'Você não tem permissão para executar esta ação.' };
    }

    if (status === 404) {
      return { kind: 'not-found', message: 'Incentivo não encontrado.' };
    }

    if (status === 409) {
      // The API's own `title`/`detail` are plain English and not meant for end users —
      // only `status`/`type` are used to pick a static PT-BR message; anything not
      // recognized as this specific conflict falls back to a generic one.
      return {
        kind: 'conflict',
        message: problem?.type === 'conflict'
          ? 'Este incentivo está vinculado a uma campanha ativa ou agendada e não pode ser arquivado.'
          : 'Não foi possível concluir esta operação. Tente novamente.',
      };
    }

    if (status === 422) {
      return {
        fieldErrors: problem?.validation_errors,
        kind: 'validation',
        message: problem?.detail ?? envelope?.message ?? 'Corrija os campos indicados.',
      };
    }
  }

  return { kind: 'unknown', message: getErrorMessage(error) };
}
